import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { capitalCase } from 'change-case';

import { authorizedAxiosInstance } from '@/clients';
import { GLOBAL_TRADE_DATASET_ID } from '@/shared/components/data-visualization/global-trade';
import { DataSourceType } from '@/types/api/modules/business-intelligence';

export type SearchFilterValueOption = { label: string; value: string };

export type DynamicOptionDefinition = {
    endpoint: string;
    field: 'attributes' | 'exchanges' | 'industries' | 'sectors' | 'sourceIds';
    params?: Record<string, unknown>;
    responseKey?: string;
    toOption: (value: unknown) => SearchFilterValueOption | undefined;
};

const option = (value: string, label = value): SearchFilterValueOption => ({ label, value });

const employeeHeadcount = [
    '1 - 10',
    '11 - 50',
    '51 - 250',
    '251 - 1k',
    '1k - 5k',
    '5k - 10k',
    '10k - 50k',
    '50k - 100k',
    '100k+',
];
const fiscalYearEnd = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const capitalRanges = [
    '$0 - $1M',
    '$1M - $10M',
    '$10M - $50M',
    '$50M - $100M',
    '$100M - $250M',
    '$250M - $500M',
    '$500M - $1B',
    '$1B - $10B',
    '$10B+',
];
const sectors = [
    'Basic Materials',
    'Communication Services',
    'Consumer Cyclical',
    'Consumer Defensive',
    'Energy',
    'Financial Services',
    'Healthcare',
    'Industrials',
    'Real Estate',
    'Technology',
    'Utilities',
];

const STATIC_OPTIONS: Record<string, SearchFilterValueOption[]> = {
    employeeHeadcount: employeeHeadcount.map((value) => option(value)),
    fiscalYearEnd: fiscalYearEnd.map((value) => option(value)),
    fundraising: capitalRanges.map((value) => option(value)),
    // Sectors are a stable FMP taxonomy. Keeping the canonical catalog locally
    // prevents the applied-filter editor from degrading to an empty menu while
    // the provider catalog is refreshing or temporarily unavailable.
    sectors: sectors.map((value) => option(value)),
    webTraffic: ['very_high', 'high', 'medium', 'medium_low', 'low', 'very_low'].map((value) => {
        const displayValue = capitalCase(value);

        return option(displayValue);
    }),
};

const getStringProperty = (value: unknown, property: string): string | undefined => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return undefined;
    }

    const propertyValue = (value as Record<string, unknown>)[property];

    return typeof propertyValue === 'string' && propertyValue.trim() ? propertyValue.trim() : undefined;
};

const DYNAMIC_OPTIONS: DynamicOptionDefinition[] = [
    {
        endpoint: '/api/vendors/fmp/exchanges',
        field: 'exchanges',
        toOption: (value) => {
            const exchange = getStringProperty(value, 'exchange');
            const name = getStringProperty(value, 'name');

            return exchange ? option(exchange, name ? `${name} (${exchange})` : exchange) : undefined;
        },
    },
    {
        endpoint: '/api/vendors/fmp/industries',
        field: 'industries',
        toOption: (value) => {
            const industry = getStringProperty(value, 'industry');

            return industry ? option(industry) : undefined;
        },
    },
    {
        endpoint: '/api/vendors/fmp/sectors',
        field: 'sectors',
        toOption: (value) => {
            const sector = getStringProperty(value, 'sector');

            return sector ? option(sector) : undefined;
        },
    },
    {
        endpoint: '/api/vendors/clearbit/company-tags',
        field: 'attributes',
        toOption: (value) => (typeof value === 'string' && value.trim() ? option(value.trim()) : undefined),
    },
    {
        endpoint: '/api/datasets/schema',
        field: 'sourceIds',
        params: { category: GLOBAL_TRADE_DATASET_ID, type: DataSourceType.GLOBAL_TRADE },
        responseKey: 'dataSources',
        toOption: (value) => {
            const key = getStringProperty(value, 'key');
            const label = getStringProperty(value, 'label');

            return key ? option(key, label ?? key) : undefined;
        },
    },
];

const canonicalField = (path: string) => path.split('.').at(-1) ?? path;

const getResponseValues = (response: unknown, definition: DynamicOptionDefinition): unknown => {
    if (Array.isArray(response)) {
        return response;
    }
    if (typeof response !== 'object' || response === null) {
        return undefined;
    }

    const responseRecord = response as Record<string, unknown>;

    if (definition.responseKey && Array.isArray(responseRecord[definition.responseKey])) {
        return responseRecord[definition.responseKey];
    }
    if (Array.isArray(responseRecord.data)) {
        return responseRecord.data;
    }

    return undefined;
};

export const normalizeSearchFilterValueOptions = (
    response: unknown,
    definition: DynamicOptionDefinition,
): SearchFilterValueOption[] => {
    const values = getResponseValues(response, definition);

    if (!Array.isArray(values)) {
        return [];
    }

    return Array.from(
        new Map(
            values
                .map(definition.toOption)
                .filter((value): value is SearchFilterValueOption => Boolean(value))
                .map((value) => [value.value, value]),
        ).values(),
    ).sort((first, second) => first.label.localeCompare(second.label));
};

/**
 * Supplies the same option catalog used by Search's full filter controls to
 * compact, already-applied filter rows. Dynamic providers are fetched only
 * while their corresponding filter is present and are cached by React Query.
 */
export const useSearchFilterValueOptions = (activePaths: readonly string[]) => {
    const activeFields = useMemo(() => new Set(activePaths.map(canonicalField)), [activePaths]);
    const queries = useQueries({
        queries: DYNAMIC_OPTIONS.map((definition) => ({
            enabled: activeFields.has(definition.field),
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
                const response = await authorizedAxiosInstance.get(definition.endpoint, {
                    params: definition.params,
                    signal,
                });

                return normalizeSearchFilterValueOptions(response.data, definition);
            },
            queryKey: ['search', 'filter-value-options', definition.field],
            staleTime: 10 * 60 * 1_000,
        })),
    });

    return useMemo(() => {
        const optionsByField: Record<string, SearchFilterValueOption[]> = { ...STATIC_OPTIONS };
        const loadingFields = new Set<string>();

        DYNAMIC_OPTIONS.forEach((definition, index) => {
            const query = queries[index];
            const data = query.data as SearchFilterValueOption[] | undefined;

            if (data?.length) {
                optionsByField[definition.field] = data;
            }
            // Cached options remain interactive during background refreshes.
            if (query.isLoading && !data?.length) {
                loadingFields.add(definition.field);
            }
        });

        return {
            isLoading: (path: string) => loadingFields.has(canonicalField(path)),
            optionsFor: (path: string) => optionsByField[canonicalField(path)],
        };
    }, [queries]);
};
